import { useCallback, useRef, useState } from 'react'
import type { OrganisationAgent, OrganisationFrame, OrganisationMachine, OrganisationRoom } from '../api/types'
import { useEventStream } from './useLiveMessages'

const FRAMES: readonly OrganisationFrame['name'][] = ['rooms', 'agents', 'machines', 'direct', 'synced']

/** Attach a listener to every frame the organisation stream delivers; call what it returns to detach. */
export type OnOrganisationFrame = (listener: (frame: OrganisationFrame) => void) => () => void

export interface OrganisationStream {
  /** Every room with your unread count and its blocker, or null until the first frame. */
  rooms: OrganisationRoom[] | null
  /** Your agents that are live, or wait on you, or null until the first frame. */
  agents: OrganisationAgent[] | null
  /** Your machines and whether each runner is up, or null until the first frame. */
  machines: OrganisationMachine[] | null
  onFrame: OnOrganisationFrame
}

/**
 * What the sidebar shows for the open organisation, pushed as it changes. One
 * stream per tab, closed and reopened when the organisation changes, and
 * nothing from the last organisation outlives the switch.
 */
export function useOrganisationStream(slug: string | null): OrganisationStream {
  const [latest, setLatest] = useState<{ slug: string | null } & Omit<OrganisationStream, 'onFrame'>>(
    { slug: null, rooms: null, agents: null, machines: null })
  const listeners = useRef(new Set<(frame: OrganisationFrame) => void>())

  useEventStream(slug && `/v1/organisations/${encodeURIComponent(slug)}/events`, FRAMES, {
    deliver: (raw) => {
      const frame = raw as OrganisationFrame
      if (frame.name === 'rooms' || frame.name === 'agents' || frame.name === 'machines') {
        setLatest((previous) => ({
          ...(previous.slug === slug ? previous : { rooms: null, agents: null, machines: null }),
          slug,
          [frame.name]: frame.data,
        }))
      }
      for (const listener of listeners.current) listener(frame)
    },
  })

  const onFrame = useCallback<OnOrganisationFrame>((listener) => {
    listeners.current.add(listener)
    return () => listeners.current.delete(listener)
  }, [])

  const current = latest.slug === slug
  return {
    rooms: current ? latest.rooms : null,
    agents: current ? latest.agents : null,
    machines: current ? latest.machines : null,
    onFrame,
  }
}
